import { useState } from 'react';
import {
  Eye,
  Download,
  UploadCloud,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
  Trash2,
  Calendar,
  User,
  Layers,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import {
  DRAWING_CATEGORIES,
  STANDARD_DRAWING_ORDER,
  type DrawingCategory,
  type ProjectDrawingRow,
  useProjectDrawings,
  useDeleteDrawing,
  formatFileSize,
  triggerFileDownload,
} from '../lib/project-drawings';
import { formatDate, TableSkeleton, ErrorState, useToast } from './ui';
import Confirm from './Confirm';
import DrawingViewerModal from './DrawingViewerModal';
import DrawingUploadDrawer from './DrawingUploadDrawer';

interface Props {
  projectId: string;
  projectName?: string;
}

export default function ProjectDrawingsLedger({ projectId, projectName }: Props) {
  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);
  const toast = useToast();

  const { data, isLoading, error, refetch } = useProjectDrawings(projectId);

  const [viewingDrawing, setViewingDrawing] = useState<ProjectDrawingRow | null>(null);
  const [uploadCategory, setUploadCategory] = useState<DrawingCategory | null>(null);
  const [expandedHistory, setExpandedHistory] = useState<Record<DrawingCategory, boolean>>({
    floor_plan: false,
    structural_details: false,
    working_drawing: false,
    elevation: false,
  });

  const [deleteTarget, setDeleteTarget] = useState<ProjectDrawingRow | null>(null);
  const deleteMutation = useDeleteDrawing(projectId);

  if (isLoading) {
    return (
      <div className="table-wrap" style={{ padding: 20 }}>
        <TableSkeleton rows={3} cols={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-wrap" style={{ padding: 20 }}>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  const latest = data?.latest ?? {
    floor_plan: null,
    structural_details: null,
    working_drawing: null,
    elevation: null,
  };

  const history = data?.historyByCategory ?? {
    floor_plan: [],
    structural_details: [],
    working_drawing: [],
    elevation: [],
  };

  function toggleHistory(cat: DrawingCategory) {
    setExpandedHistory((prev) => ({ ...prev, [cat]: !prev[cat] }));
  }

  async function doDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({
        id: deleteTarget.id,
        category: deleteTarget.category,
        targetProjectId: projectId,
      });
      toast.show(`Drawing revision v${deleteTarget.version} deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not delete drawing', true);
    }
  }

  function handleDownload(d: ProjectDrawingRow) {
    triggerFileDownload(d.fileUrl, d.fileName, d.fileType);
  }

  return (
    <div style={{ padding: '20px 24px' }}>
      {/* Header Info */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
            Project Drawings & Architectural Plans
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-soft)', margin: '4px 0 0' }}>
            Store, view, and version floor plans, structural layouts, and working drawings. New uploads become active while retaining revision history.
          </p>
        </div>

        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setUploadCategory('floor_plan')}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <UploadCloud size={14} />
          <span>Upload Drawing</span>
        </button>
      </div>

      {/* 3 Main Drawing Category Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          gap: 20,
        }}
      >
        {STANDARD_DRAWING_ORDER.map((catKey) => {
          const cat = DRAWING_CATEGORIES[catKey];
          const current = latest[catKey];
          const catHistory = history[catKey] ?? [];
          const isExpanded = expandedHistory[catKey];

          const isImage =
            current &&
            (current.fileType.startsWith('image/') ||
              current.fileUrl.startsWith('data:image/') ||
              /\.(png|jpe?g|webp|gif|svg)$/i.test(current.fileName));
          const isPdf =
            current &&
            (current.fileType === 'application/pdf' ||
              current.fileUrl.startsWith('data:application/pdf') ||
              /\.pdf$/i.test(current.fileName));

          return (
            <div
              key={catKey}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'var(--shadow-xs)',
                transition: 'all 0.2s ease',
              }}
            >
              {/* Category Card Header */}
              <div
                style={{
                  padding: '14px 18px',
                  background: 'var(--surface-2)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: cat.color,
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ fontWeight: 700, fontSize: 14.5, color: 'var(--text)' }}>
                    {cat.label}
                  </span>
                </div>

                {current && (
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: '#059669',
                      background: '#ecfdf5',
                      padding: '2px 8px',
                      borderRadius: 12,
                      border: '1px solid #a7f3d0',
                    }}
                  >
                    v{current.version} Active
                  </span>
                )}
              </div>

              {/* Category Card Body */}
              <div style={{ padding: 18, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {current ? (
                  <>
                    {/* Visual Preview Box */}
                    <div
                      onClick={() => setViewingDrawing(current)}
                      style={{
                        height: 180,
                        borderRadius: 'var(--radius-sm)',
                        background: isImage ? '#0f172a' : '#f8fafc',
                        border: '1px solid var(--border)',
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 14,
                      }}
                      className="drawing-preview-box"
                      title="Click to view fullscreen"
                    >
                      {isImage ? (
                        <img
                          src={current.fileUrl}
                          alt={current.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease',
                          }}
                        />
                      ) : isPdf ? (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                          <FileText size={44} style={{ color: '#dc2626', marginBottom: 8 }} />
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                            PDF Document
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 2 }}>
                            {current.fileName}
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                          <FileText size={44} style={{ color: 'var(--text-soft)', marginBottom: 8 }} />
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                            {current.fileName}
                          </div>
                        </div>
                      )}

                      {/* Hover eye icon overlay */}
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(15, 23, 42, 0.45)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: 13,
                          opacity: 0.9,
                          transition: 'opacity 0.2s ease',
                        }}
                      >
                        <Eye size={18} />
                        <span>Click to View</span>
                      </div>
                    </div>

                    {/* Metadata details */}
                    <div style={{ marginBottom: 14, flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: 'var(--text)',
                          marginBottom: 4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {current.title}
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--text-soft)',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 12,
                          marginBottom: 8,
                        }}
                      >
                        <span>{formatDate(current.createdAt)}</span>
                        <span>•</span>
                        <span>{formatFileSize(current.fileSize)}</span>
                        {current.uploadedByName && (
                          <>
                            <span>•</span>
                            <span>{current.uploadedByName}</span>
                          </>
                        )}
                      </div>

                      {current.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--text)',
                            background: 'var(--surface-2)',
                            padding: '6px 10px',
                            borderRadius: 'var(--radius-xs)',
                            border: '1px solid var(--border-subtle)',
                            lineHeight: 1.4,
                          }}
                        >
                          {current.description}
                        </div>
                      )}
                    </div>

                    {/* Main action buttons */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        paddingTop: 10,
                        borderTop: '1px solid var(--border)',
                      }}
                    >
                      <button
                        type="button"
                        className="btn btn-auto btn-sm btn-ghost"
                        onClick={() => setViewingDrawing(current)}
                        style={{
                          flex: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          fontWeight: 600,
                        }}
                      >
                        <Eye size={14} />
                        <span>View</span>
                      </button>

                      <button
                        type="button"
                        className="btn btn-auto btn-sm btn-ghost"
                        onClick={() => handleDownload(current)}
                        title="Download drawing file"
                      >
                        <Download size={14} />
                      </button>

                      {admin && (
                        <button
                          type="button"
                          className="btn btn-auto btn-sm btn-ghost"
                          onClick={() => setDeleteTarget(current)}
                          title="Delete active drawing from database"
                          style={{
                            color: '#dc2626',
                            padding: '6px 8px',
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn btn-auto btn-sm btn-primary"
                        onClick={() => setUploadCategory(catKey)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        title="Upload a new revision"
                      >
                        <UploadCloud size={13} />
                        <span>+ Revision</span>
                      </button>
                    </div>

                    {/* Revision History Accordion */}
                    {catHistory.length > 1 && (
                      <div style={{ marginTop: 12, borderTop: '1px dashed var(--border)', paddingTop: 10 }}>
                        <button
                          type="button"
                          onClick={() => toggleHistory(catKey)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--accent)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            padding: '4px 0',
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Clock size={13} />
                            <span>Revision History ({catHistory.length} versions)</span>
                          </span>
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>

                        {isExpanded && (
                          <div
                            style={{
                              marginTop: 8,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 6,
                              maxHeight: 180,
                              overflowY: 'auto',
                            }}
                          >
                            {catHistory.map((rev: ProjectDrawingRow) => (
                              <div
                                key={rev.id}
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: 'var(--radius-xs)',
                                  background: rev.isLatest ? `${cat.bg}` : 'var(--surface-2)',
                                  border: `1px solid ${rev.isLatest ? cat.borderColor : 'var(--border)'}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  fontSize: 12,
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                                    v{rev.version} — {rev.title}
                                    {rev.isLatest && (
                                      <span
                                        style={{
                                          marginLeft: 6,
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: '#059669',
                                        }}
                                      >
                                        (Active)
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ color: 'var(--text-soft)', fontSize: 11, marginTop: 1 }}>
                                    {formatDate(rev.createdAt)} • {formatFileSize(rev.fileSize)}
                                  </div>
                                </div>

                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button
                                    type="button"
                                    className="icon-btn"
                                    onClick={() => setViewingDrawing(rev)}
                                    title="View this version"
                                    style={{ width: 24, height: 24 }}
                                  >
                                    <Eye size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    className="icon-btn"
                                    onClick={() => handleDownload(rev)}
                                    title="Download this version"
                                    style={{ width: 24, height: 24 }}
                                  >
                                    <Download size={12} />
                                  </button>
                                  {admin && (
                                    <button
                                      type="button"
                                      className="icon-btn btn-danger"
                                      onClick={() => setDeleteTarget(rev)}
                                      title="Delete this revision"
                                      style={{ width: 24, height: 24 }}
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  /* Empty state for this drawing category */
                  <div
                    style={{
                      flex: 1,
                      border: '2px dashed var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '30px 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      background: 'var(--surface-2)',
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: cat.bg,
                        color: cat.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 10,
                      }}
                    >
                      <Layers size={20} />
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text)', marginBottom: 4 }}>
                      No {cat.label} uploaded
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-soft)', margin: '0 0 14px', maxWidth: 220 }}>
                      {cat.description}
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary btn-auto btn-sm"
                      onClick={() => setUploadCategory(catKey)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <UploadCloud size={13} />
                      <span>Upload {cat.label}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Upload Drawer */}
      {uploadCategory && (
        <DrawingUploadDrawer
          key={uploadCategory}
          projectId={projectId}
          projectName={projectName}
          initialCategory={uploadCategory}
          onClose={() => setUploadCategory(null)}
        />
      )}

      {/* Fullscreen Viewer Modal */}
      {viewingDrawing && (
        <DrawingViewerModal
          initialDrawing={viewingDrawing}
          allVersions={data?.all ?? []}
          projectName={projectName}
          onClose={() => setViewingDrawing(null)}
          onUploadNewRevision={(cat) => setUploadCategory(cat)}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <Confirm
          title={`Delete Revision v${deleteTarget.version} from Database?`}
          message="This drawing file will be deleted from the database. If this is the active version, the most recent previous revision will automatically become the active drawing."
          busy={deleteMutation.isPending}
          onConfirm={() => void doDelete()}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
